/**
 * Project Detail screen — issue #37 + #50.
 *
 * Reads :id from useParams, loads project + milestones + risks via api.call.
 * Tabs: Timeline, Documents, Quotes, Budget, Risks, Knowledge.
 *
 * #50 changes:
 *  - Fix (A unwrap): GET /projects/:id returns { project, health } — unwrap correctly.
 *  - A6: Fetch GET /agents/development/health/:projectId and show "Agent Assessment"
 *        sidebar card (loading-only inside the card; never blocks the page).
 *  - A10: Enable Knowledge tab — wire POST /brain with project context prefix.
 *  - A9: Add Resolve button on each risk; POST /decisions then POST /risks/:id/resolve.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAINative } from "../providers/AINativeProvider.js";
import type {
  ProjectDetail,
  Milestone,
  Risk,
  ProjectDocument,
  Quote,
  BudgetLine,
} from "./projects/types.js";
import {
  formatCurrency,
  formatDate,
  healthClass,
  healthLabel,
  milestoneStatusClass,
  docTypeIcon,
  safeApiCall,
} from "./projects/utils.js";
import "./projects/projects.css";

// ─── Tab definition ───────────────────────────────────────────────────────────

type TabKey = "timeline" | "documents" | "quotes" | "budget" | "risks" | "knowledge";

const TABS: { key: TabKey; label: string }[] = [
  { key: "timeline", label: "Timeline" },
  { key: "documents", label: "Documents" },
  { key: "quotes", label: "Quotes" },
  { key: "budget", label: "Budget" },
  { key: "risks", label: "Risks" },
  { key: "knowledge", label: "Knowledge" },
];

// ─── A6: Agent health types ───────────────────────────────────────────────────

interface AgentHealth {
  projectId?: string;
  health?: string;
  score?: number;
  riskFactors?: string[];
  recommendations?: string[];
  nextSteps?: string[];
  summary?: string;
}

// ─── A10: Brain / chat types ──────────────────────────────────────────────────

interface BrainResult {
  id: string;
  content: string;
  score: number;
  entity_id: string | null;
}

interface BrainResponse {
  answer?: string;
  context?: string;
  results?: BrainResult[];
  citations?: unknown[];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TabBar({
  active,
  onChange,
}: {
  active: TabKey;
  onChange: (tab: TabKey) => void;
}) {
  return (
    <div className="tab-bar" role="tablist" aria-label="Project sections">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          aria-controls={`tabpanel-${tab.key}`}
          id={`tab-${tab.key}`}
          className={`tab-btn ${active === tab.key ? "tab-btn--active" : ""}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Timeline tab ─────────────────────────────────────────────────────────────

// ─── #43: Milestone write helpers ─────────────────────────────────────────────
//
// The backend milestone status enum is pending | in-progress | completed |
// blocked. The frontend Milestone type uses not-started | in-progress | done |
// at-risk. We map between them at the API boundary so timeline rendering and
// backend writes both stay correct.

/** Map a backend milestone status to the UI Milestone.status enum. */
function toUiMilestoneStatus(backend: string | undefined): Milestone["status"] {
  switch (backend) {
    case "completed":
      return "done";
    case "blocked":
      return "at-risk";
    case "in-progress":
      return "in-progress";
    default:
      return "not-started";
  }
}

/** Raw shape of the milestone write responses — { milestone: {...} }. */
interface MilestoneWriteResponse {
  milestone?: Record<string, unknown>;
}

function AddMilestoneForm({
  projectRef,
  onMilestoneAdded,
  api,
}: {
  projectRef: string;
  onMilestoneAdded: (m: Milestone) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setDueDate("");
    setErrorMsg(null);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const resp = await api.call<MilestoneWriteResponse>(
        `/projects/${encodeURIComponent(projectRef)}/milestones`,
        {
          method: "POST",
          body: {
            name: trimmed,
            ...(dueDate ? { due_date: dueDate } : {}),
            status: "pending",
          },
        },
      );
      const row = resp.milestone ?? {};
      const newMilestone: Milestone = {
        id: (row["entity_id"] as string) ?? (row["id"] as string) ?? `milestone:${trimmed.slice(0, 8)}`,
        projectId: projectRef,
        name: (row["name"] as string) ?? trimmed,
        status: toUiMilestoneStatus((row["status"] as string) ?? "pending"),
        ...(dueDate ? { dueDate } : {}),
      };
      onMilestoneAdded(newMilestone);
      reset();
      setOpen(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to add milestone");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        className="milestone-add-btn"
        onClick={() => setOpen(true)}
        aria-label="Add a milestone"
      >
        + Add Milestone
      </button>
    );
  }

  return (
    <div className="milestone-add-form" role="region" aria-label="Add a milestone">
      <label className="milestone-add-form__label" htmlFor="add-milestone-name">
        Milestone name
      </label>
      <input
        id="add-milestone-name"
        type="text"
        className="milestone-add-form__input"
        placeholder="e.g. Foundation pour complete"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={submitting}
        aria-label="Milestone name"
      />
      <label className="milestone-add-form__label" htmlFor="add-milestone-due">
        Due date (optional)
      </label>
      <input
        id="add-milestone-due"
        type="date"
        className="milestone-add-form__input"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        disabled={submitting}
        aria-label="Milestone due date"
      />
      {errorMsg && (
        <div className="project-chat__error" role="alert">
          <span aria-hidden="true">!</span>
          {errorMsg}
        </div>
      )}
      <div className="milestone-add-form__actions">
        <button
          className="risk-resolve-panel__cancel"
          onClick={() => { setOpen(false); reset(); }}
          disabled={submitting}
          aria-label="Cancel add milestone"
        >
          Cancel
        </button>
        <button
          className="risk-resolve-panel__submit"
          onClick={() => { void handleSubmit(); }}
          disabled={submitting || !name.trim()}
          aria-label="Save milestone"
        >
          {submitting ? "Adding..." : "Add Milestone"}
        </button>
      </div>
    </div>
  );
}

/** Inline "Mark complete" control for a non-completed milestone. */
function MilestoneCompleteButton({
  milestone,
  onMilestoneUpdated,
  api,
}: {
  milestone: Milestone;
  onMilestoneUpdated: (m: Milestone) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const markComplete = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const naturalKey = milestone.id.startsWith("milestone:")
        ? milestone.id.slice("milestone:".length)
        : milestone.id;
      await api.call(`/milestones/${encodeURIComponent(naturalKey)}`, {
        method: "PATCH",
        body: { status: "completed" },
      });
      onMilestoneUpdated({ ...milestone, status: "done" });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  if (milestone.status === "done") return null;

  return (
    <div className="milestone-complete">
      <button
        className="milestone-complete__btn"
        onClick={() => { void markComplete(); }}
        disabled={submitting}
        aria-label={`Mark milestone complete: ${milestone.name}`}
      >
        {submitting ? "..." : "✓ Complete"}
      </button>
      {errorMsg && (
        <span className="milestone-complete__error" role="alert">
          {errorMsg}
        </span>
      )}
    </div>
  );
}

function MilestoneTimeline({
  milestones,
  loading,
  error,
  projectRef,
  onMilestoneAdded,
  onMilestoneUpdated,
  api,
}: {
  milestones: Milestone[];
  loading: boolean;
  error: string | null;
  projectRef: string;
  onMilestoneAdded: (m: Milestone) => void;
  onMilestoneUpdated: (m: Milestone) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  if (loading) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Milestones</span>
        </div>
        <div className="panel__body" style={{ padding: "16px 18px" }}>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="skeleton skeleton-row" style={{ marginBottom: 8 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Milestones</span>
        </div>
        <div className="panel__body">
          <div className="error-banner" style={{ margin: "16px 18px" }} role="alert">
            <span className="error-banner__icon">⚠</span>
            <span className="error-banner__text">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (milestones.length === 0) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Milestones</span>
        </div>
        <div className="panel__body">
          <div className="empty-state" style={{ border: "none" }}>
            <div className="empty-state__icon">📍</div>
            <div className="empty-state__title">No milestones</div>
            <div className="empty-state__desc">
              Milestones will appear here once added to this project.
            </div>
          </div>
          <div className="timeline__footer">
            <AddMilestoneForm
              projectRef={projectRef}
              onMilestoneAdded={onMilestoneAdded}
              api={api}
            />
          </div>
        </div>
      </div>
    );
  }

  // Build a lookup for dependency names
  const byId: Record<string, Milestone> = {};
  for (const m of milestones) byId[m.id] = m;

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Milestones</span>
        <span className="panel__count">{milestones.length}</span>
      </div>
      <div className="panel__body">
        <ol className="timeline" aria-label="Project milestones">
          {milestones.map((m, idx) => {
            const dotClass = milestoneStatusClass(m.status);
            const isLast = idx === milestones.length - 1;
            return (
              <li key={m.id} className="timeline__item">
                <div className="timeline__track" aria-hidden="true">
                  <span className={`timeline__dot ${dotClass}`} />
                  {!isLast && <span className="timeline__line" />}
                </div>
                <div className="timeline__content">
                  <div className="timeline__name">{m.name}</div>
                  <div className="timeline__row">
                    {m.dueDate && (
                      <span className="timeline__date">Due {formatDate(m.dueDate)}</span>
                    )}
                    {m.completedAt && (
                      <span className="timeline__date">
                        Completed {formatDate(m.completedAt)}
                      </span>
                    )}
                    <span className="status-chip">{m.status.replace("-", " ")}</span>
                  </div>
                  {m.dependsOn && m.dependsOn.length > 0 && (
                    <div className="timeline__row" style={{ marginTop: 4 }}>
                      {m.dependsOn.map((depId) => (
                        <span key={depId} className="timeline__dep">
                          depends on: {byId[depId]?.name ?? depId}
                        </span>
                      ))}
                    </div>
                  )}
                  <MilestoneCompleteButton
                    milestone={m}
                    onMilestoneUpdated={onMilestoneUpdated}
                    api={api}
                  />
                </div>
              </li>
            );
          })}
        </ol>
        <div className="timeline__footer">
          <AddMilestoneForm
            projectRef={projectRef}
            onMilestoneAdded={onMilestoneAdded}
            api={api}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Documents tab (full CRUD) ────────────────────────────────────────────────

type Api = ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];

/** Extract the natural key from a `type:key` entity_id (or return as-is). */
function naturalKey(id: string): string {
  const idx = id.indexOf(":");
  return idx === -1 ? id : id.slice(idx + 1);
}

/**
 * Resolve the canonical id for a documents/quotes/budget item. Rows created
 * optimistically carry `id`, but rows loaded from GET /projects/:id come back
 * with `entity_id` and no `id`. Prefer whichever is present so delete/edit and
 * React keys work in both cases (fixes: delete no-op after reload).
 */
function itemId(x: { id?: string; entity_id?: string }): string {
  return x.id ?? x.entity_id ?? "";
}

const DOCUMENT_TYPES = ["permit", "plan", "survey", "contract", "inspection", "invoice", "other"];

function DocumentsTab({
  project,
  projectRef,
  api,
}: {
  project: ProjectDetail;
  projectRef: string;
  api: Api;
}) {
  const [docs, setDocs] = useState<ProjectDocument[]>(project.documents ?? []);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("permit");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => { setName(""); setType("permit"); setUrl(""); setError(null); };

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true); setError(null);
    try {
      const resp = await api.call<{ document?: Record<string, unknown> }>(
        `/projects/${encodeURIComponent(projectRef)}/documents`,
        { method: "POST", body: { name: trimmed, type, ...(url.trim() ? { url: url.trim() } : {}) } },
      );
      const row = resp.document ?? {};
      const created: ProjectDocument = {
        id: (row["entity_id"] as string) ?? (row["id"] as string) ?? `document:${trimmed.slice(0, 8)}`,
        projectId: projectRef,
        name: (row["name"] as string) ?? trimmed,
        type: ((row["type"] as string) ?? type) as ProjectDocument["type"],
        ...(url.trim() ? { url: url.trim() } : {}),
      };
      setDocs((prev) => [...prev, created]);
      resetForm(); setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add document");
    } finally { setBusy(false); }
  };

  const remove = async (doc: ProjectDocument) => {
    const prev = docs;
    const did = itemId(doc);
    setDocs((d) => d.filter((x) => itemId(x) !== did)); // optimistic
    try {
      await api.call(`/documents/${encodeURIComponent(naturalKey(did))}`, { method: "DELETE" });
    } catch {
      setDocs(prev); // revert
    }
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Documents</span>
        <span className="panel__count">{docs.length}</span>
        {!adding && (
          <button className="milestone-add-btn" onClick={() => setAdding(true)} aria-label="Add a document">
            + Add Document
          </button>
        )}
      </div>
      <div className="panel__body">
        {adding && (
          <div className="milestone-add-form" role="region" aria-label="Add a document">
            <label className="milestone-add-form__label" htmlFor="add-doc-name">Document name</label>
            <input id="add-doc-name" type="text" className="milestone-add-form__input"
              placeholder="e.g. Building permit #2024-1188" value={name}
              onChange={(e) => setName(e.target.value)} disabled={busy} aria-label="Document name" />
            <label className="milestone-add-form__label" htmlFor="add-doc-type">Type</label>
            <select id="add-doc-type" className="milestone-add-form__input" value={type}
              onChange={(e) => setType(e.target.value)} disabled={busy} aria-label="Document type">
              {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="milestone-add-form__label" htmlFor="add-doc-url">URL (optional)</label>
            <input id="add-doc-url" type="text" className="milestone-add-form__input"
              placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)}
              disabled={busy} aria-label="Document URL" />
            {error && <div className="project-chat__error" role="alert"><span aria-hidden="true">!</span>{error}</div>}
            <div className="milestone-add-form__actions">
              <button className="risk-resolve-panel__cancel" onClick={() => { setAdding(false); resetForm(); }}
                disabled={busy} aria-label="Cancel add document">Cancel</button>
              <button className="risk-resolve-panel__submit" onClick={() => { void add(); }}
                disabled={busy || !name.trim()} aria-label="Save document">
                {busy ? "Adding..." : "Add Document"}
              </button>
            </div>
          </div>
        )}
        {docs.length === 0 && !adding ? (
          <div className="empty-state">
            <div className="empty-state__icon">📂</div>
            <div className="empty-state__title">No documents</div>
            <div className="empty-state__desc">
              Architectural plans, permits, surveys and other documents will appear here.
            </div>
          </div>
        ) : (
          <ul className="doc-list" aria-label="Project documents">
            {docs.map((doc) => (
              <li key={itemId(doc)} className="doc-item">
                <span className="doc-icon" aria-hidden="true">{docTypeIcon(doc.type)}</span>
                <div className="doc-info">
                  <div className="doc-name">{doc.name}</div>
                  {doc.uploadedAt && <div className="doc-meta">Uploaded {formatDate(doc.uploadedAt)}</div>}
                </div>
                <span className="doc-type-badge">{doc.type}</span>
                <button className="doc-delete-btn" onClick={() => { void remove(doc); }}
                  aria-label={`Delete ${doc.name}`} title="Delete">✕</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Quotes tab ───────────────────────────────────────────────────────────────

const QUOTE_STATUSES: Quote["status"][] = ["pending", "accepted", "rejected"];

function QuotesTab({
  project,
  projectRef,
  api,
}: {
  project: ProjectDetail;
  projectRef: string;
  api: Api;
}) {
  const [quotes, setQuotes] = useState<Quote[]>(project.quotes ?? []);
  const [adding, setAdding] = useState(false);
  const [vendor, setVendor] = useState("");
  const [scope, setScope] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => { setVendor(""); setScope(""); setAmount(""); setError(null); };

  const add = async () => {
    const v = vendor.trim();
    const amt = Number(amount);
    if (!v || !Number.isFinite(amt) || amt < 0) {
      setError("Vendor and a valid amount are required");
      return;
    }
    setBusy(true); setError(null);
    try {
      const resp = await api.call<{ quote?: Record<string, unknown> }>(
        `/projects/${encodeURIComponent(projectRef)}/quotes`,
        { method: "POST", body: { vendor: v, scope: scope.trim(), amount: amt, status: "pending" } },
      );
      const row = resp.quote ?? {};
      const created: Quote = {
        id: (row["entity_id"] as string) ?? (row["id"] as string) ?? `quote:${v.slice(0, 8)}`,
        projectId: projectRef,
        vendor: (row["vendor"] as string) ?? v,
        scope: (row["scope"] as string) ?? scope.trim(),
        amount: (row["amount"] as number) ?? amt,
        status: ((row["status"] as string) ?? "pending") as Quote["status"],
      };
      setQuotes((prev) => [...prev, created]);
      resetForm(); setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add quote");
    } finally { setBusy(false); }
  };

  const setStatus = async (q: Quote, status: Quote["status"]) => {
    const prev = quotes;
    const qid = itemId(q);
    setQuotes((qs) => qs.map((x) => (itemId(x) === qid ? { ...x, status } : x))); // optimistic
    try {
      await api.call(`/quotes/${encodeURIComponent(naturalKey(qid))}`, { method: "PATCH", body: { status } });
    } catch {
      setQuotes(prev);
    }
  };

  const remove = async (q: Quote) => {
    const prev = quotes;
    const qid = itemId(q);
    setQuotes((qs) => qs.filter((x) => itemId(x) !== qid));
    try {
      await api.call(`/quotes/${encodeURIComponent(naturalKey(qid))}`, { method: "DELETE" });
    } catch {
      setQuotes(prev);
    }
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Quotes</span>
        <span className="panel__count">{quotes.length}</span>
        {!adding && (
          <button className="milestone-add-btn" onClick={() => setAdding(true)} aria-label="Add a quote">
            + Add Quote
          </button>
        )}
      </div>
      <div className="panel__body">
        {adding && (
          <div className="milestone-add-form" role="region" aria-label="Add a quote">
            <label className="milestone-add-form__label" htmlFor="add-quote-vendor">Vendor</label>
            <input id="add-quote-vendor" type="text" className="milestone-add-form__input"
              placeholder="e.g. Bay Area Concrete Co." value={vendor}
              onChange={(e) => setVendor(e.target.value)} disabled={busy} aria-label="Quote vendor" />
            <label className="milestone-add-form__label" htmlFor="add-quote-scope">Scope</label>
            <input id="add-quote-scope" type="text" className="milestone-add-form__input"
              placeholder="e.g. Foundation & slab" value={scope}
              onChange={(e) => setScope(e.target.value)} disabled={busy} aria-label="Quote scope" />
            <label className="milestone-add-form__label" htmlFor="add-quote-amount">Amount (USD)</label>
            <input id="add-quote-amount" type="number" min="0" className="milestone-add-form__input"
              placeholder="e.g. 85000" value={amount}
              onChange={(e) => setAmount(e.target.value)} disabled={busy} aria-label="Quote amount" />
            {error && <div className="project-chat__error" role="alert"><span aria-hidden="true">!</span>{error}</div>}
            <div className="milestone-add-form__actions">
              <button className="risk-resolve-panel__cancel" onClick={() => { setAdding(false); resetForm(); }}
                disabled={busy} aria-label="Cancel add quote">Cancel</button>
              <button className="risk-resolve-panel__submit" onClick={() => { void add(); }}
                disabled={busy || !vendor.trim()} aria-label="Save quote">
                {busy ? "Adding..." : "Add Quote"}
              </button>
            </div>
          </div>
        )}
        {quotes.length === 0 && !adding ? (
          <div className="empty-state">
            <div className="empty-state__icon">💬</div>
            <div className="empty-state__title">No quotes</div>
            <div className="empty-state__desc">
              Contractor and vendor quotes will be compared here once received.
            </div>
          </div>
        ) : (
          <ul className="quote-list" aria-label="Project quotes">
            {quotes.map((q) => (
              <li key={itemId(q)} className="quote-item">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="quote-vendor">{q.vendor}</div>
                  <div className="quote-scope">{q.scope}</div>
                </div>
                <select className="quote-status-select" value={q.status}
                  onChange={(e) => { void setStatus(q, e.target.value as Quote["status"]); }}
                  aria-label={`Status for ${q.vendor}`}>
                  {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <span className="quote-amount">{formatCurrency(q.amount)}</span>
                <button className="doc-delete-btn" onClick={() => { void remove(q); }}
                  aria-label={`Delete quote from ${q.vendor}`} title="Delete">✕</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Budget tab ───────────────────────────────────────────────────────────────

function BudgetTab({
  project,
  projectRef,
  api,
}: {
  project: ProjectDetail;
  projectRef: string;
  api: Api;
}) {
  const [lines, setLines] = useState<BudgetLine[]>(project.budgetLines ?? []);
  const [adding, setAdding] = useState(false);
  const [category, setCategory] = useState("");
  const [budgeted, setBudgeted] = useState("");
  const [actual, setActual] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totals = lines.reduce(
    (acc, l) => ({ budgeted: acc.budgeted + l.budgeted, actual: acc.actual + l.actual }),
    { budgeted: 0, actual: 0 },
  );
  const totalVariance = totals.actual - totals.budgeted;

  const resetForm = () => { setCategory(""); setBudgeted(""); setActual(""); setError(null); };

  const add = async () => {
    const cat = category.trim();
    const b = Number(budgeted);
    const a = actual.trim() === "" ? 0 : Number(actual);
    if (!cat || !Number.isFinite(b) || b < 0 || !Number.isFinite(a) || a < 0) {
      setError("Category and a valid budgeted amount are required");
      return;
    }
    setBusy(true); setError(null);
    try {
      const resp = await api.call<{ budgetLine?: Record<string, unknown> }>(
        `/projects/${encodeURIComponent(projectRef)}/budget`,
        { method: "POST", body: { category: cat, budgeted: b, actual: a } },
      );
      const row = resp.budgetLine ?? {};
      const created: BudgetLine = {
        id: (row["entity_id"] as string) ?? (row["id"] as string) ?? `budget:${cat.slice(0, 8)}`,
        projectId: projectRef,
        category: (row["category"] as string) ?? cat,
        budgeted: (row["budgeted"] as number) ?? b,
        actual: (row["actual"] as number) ?? a,
      };
      setLines((prev) => [...prev, created]);
      resetForm(); setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add budget line");
    } finally { setBusy(false); }
  };

  const remove = async (line: BudgetLine) => {
    const prev = lines;
    const lid = itemId(line);
    setLines((ls) => ls.filter((x) => itemId(x) !== lid));
    try {
      await api.call(`/budget/${encodeURIComponent(naturalKey(lid))}`, { method: "DELETE" });
    } catch {
      setLines(prev);
    }
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Budget</span>
        <span className="panel__count">{lines.length}</span>
        {!adding && (
          <button className="milestone-add-btn" onClick={() => setAdding(true)} aria-label="Add a budget line">
            + Add Line
          </button>
        )}
      </div>
      <div className="panel__body">
        {adding && (
          <div className="milestone-add-form" role="region" aria-label="Add a budget line">
            <label className="milestone-add-form__label" htmlFor="add-budget-cat">Category</label>
            <input id="add-budget-cat" type="text" className="milestone-add-form__input"
              placeholder="e.g. Site work" value={category}
              onChange={(e) => setCategory(e.target.value)} disabled={busy} aria-label="Budget category" />
            <label className="milestone-add-form__label" htmlFor="add-budget-budgeted">Budgeted (USD)</label>
            <input id="add-budget-budgeted" type="number" min="0" className="milestone-add-form__input"
              placeholder="e.g. 120000" value={budgeted}
              onChange={(e) => setBudgeted(e.target.value)} disabled={busy} aria-label="Budgeted amount" />
            <label className="milestone-add-form__label" htmlFor="add-budget-actual">Actual (USD, optional)</label>
            <input id="add-budget-actual" type="number" min="0" className="milestone-add-form__input"
              placeholder="e.g. 0" value={actual}
              onChange={(e) => setActual(e.target.value)} disabled={busy} aria-label="Actual amount" />
            {error && <div className="project-chat__error" role="alert"><span aria-hidden="true">!</span>{error}</div>}
            <div className="milestone-add-form__actions">
              <button className="risk-resolve-panel__cancel" onClick={() => { setAdding(false); resetForm(); }}
                disabled={busy} aria-label="Cancel add budget line">Cancel</button>
              <button className="risk-resolve-panel__submit" onClick={() => { void add(); }}
                disabled={busy || !category.trim()} aria-label="Save budget line">
                {busy ? "Adding..." : "Add Line"}
              </button>
            </div>
          </div>
        )}
        {lines.length === 0 && !adding ? (
          <div className="empty-state" style={{ border: "none" }}>
            <div className="empty-state__icon">💰</div>
            <div className="empty-state__title">No budget lines</div>
            <div className="empty-state__desc">
              Budget categories and actuals will appear here once configured.
            </div>
          </div>
        ) : lines.length > 0 ? (
          <table className="budget-table" aria-label="Project budget">
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Budgeted</th>
                <th scope="col">Actual</th>
                <th scope="col">Variance</th>
                <th scope="col" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const variance = line.actual - line.budgeted;
                return (
                  <tr key={itemId(line)} className={variance > 0 ? "budget-table__over" : ""}>
                    <td>{line.category}</td>
                    <td><span className="budget-amount">{formatCurrency(line.budgeted)}</span></td>
                    <td><span className="budget-amount">{formatCurrency(line.actual)}</span></td>
                    <td>
                      <span className={variance > 0 ? "budget-variance--neg" : "budget-variance--pos"}>
                        {variance > 0 ? "+" : ""}{formatCurrency(variance)}
                      </span>
                    </td>
                    <td>
                      <button className="doc-delete-btn" onClick={() => { void remove(line); }}
                        aria-label={`Delete ${line.category}`} title="Delete">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="budget-table__total">
                <td>Total</td>
                <td><span className="budget-amount">{formatCurrency(totals.budgeted)}</span></td>
                <td><span className="budget-amount">{formatCurrency(totals.actual)}</span></td>
                <td>
                  <span className={totalVariance > 0 ? "budget-variance--neg" : "budget-variance--pos"}>
                    {totalVariance > 0 ? "+" : ""}{formatCurrency(totalVariance)}
                  </span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        ) : null}
      </div>
    </div>
  );
}

// ─── A9: Risks tab with Resolve button ───────────────────────────────────────

interface RiskItemProps {
  risk: Risk;
  projectRef: string;
  onResolved: (riskId: string) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}

function RiskItem({ risk, projectRef, onResolved, api }: RiskItemProps) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(risk.status === "resolved");

  const handleResolveSubmit = async () => {
    if (!rationale.trim()) return;
    setSubmitting(true);
    setResolveError(null);

    try {
      // Step 1: Create a decision
      const decisionResp = await api.call<{ decision: { entity_id: string } }>("/decisions", {
        method: "POST",
        body: {
          project_ref: projectRef,
          title: `Risk resolution: ${risk.title}`,
          rationale: rationale.trim(),
          decision_type: "risk_resolution",
        },
      });

      const decisionEntityId = decisionResp.decision?.entity_id;
      if (!decisionEntityId) throw new Error("Decision created but no entity_id returned");

      // Step 2: Resolve the risk using natural key (strip "risk:" prefix)
      const riskNaturalKey = risk.id.startsWith("risk:") ? risk.id.slice(5) : risk.id;
      await api.call(`/risks/${riskNaturalKey}/resolve`, {
        method: "POST",
        body: { decision_id: decisionEntityId },
      });

      // Optimistically mark resolved
      setResolved(true);
      setResolveOpen(false);
      setRationale("");
      onResolved(risk.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to resolve risk";
      setResolveError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className={`risk-item${resolved ? " risk-item--resolved" : ""}`}>
      <span
        className={`risk-severity risk-severity--${risk.severity}`}
        aria-hidden="true"
      />
      <div className="risk-content">
        <div className="risk-title">{risk.title}</div>
        {risk.description && (
          <div className="risk-desc">{risk.description}</div>
        )}

        {/* A9: Resolve panel */}
        {resolveOpen && !resolved && (
          <div className="risk-resolve-panel" role="region" aria-label={`Resolve risk: ${risk.title}`}>
            <label className="risk-resolve-panel__label" htmlFor={`resolve-rationale-${risk.id}`}>
              Decision rationale
            </label>
            <textarea
              id={`resolve-rationale-${risk.id}`}
              className="risk-resolve-panel__textarea"
              placeholder="Describe how this risk was addressed or accepted..."
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              disabled={submitting}
              aria-label={`Decision rationale for risk: ${risk.title}`}
            />
            {resolveError && (
              <div className="project-chat__error" role="alert">
                <span aria-hidden="true">!</span>
                {resolveError}
              </div>
            )}
            <div className="risk-resolve-panel__actions">
              <button
                className="risk-resolve-panel__cancel"
                onClick={() => { setResolveOpen(false); setResolveError(null); }}
                disabled={submitting}
                aria-label="Cancel resolve"
              >
                Cancel
              </button>
              <button
                className="risk-resolve-panel__submit"
                onClick={() => { void handleResolveSubmit(); }}
                disabled={submitting || !rationale.trim()}
                aria-label="Submit resolution"
              >
                {submitting ? "Resolving..." : "Mark Resolved"}
              </button>
            </div>
          </div>
        )}
      </div>

      {resolved ? (
        <span className="risk-resolved-chip" aria-label="Risk resolved">
          Resolved
        </span>
      ) : (
        <>
          <span className={`risk-badge risk-badge--${risk.severity}`}>
            {risk.severity}
          </span>
          {!resolveOpen && (
            <button
              className="risk-resolve-btn"
              onClick={() => setResolveOpen(true)}
              aria-label={`Resolve risk: ${risk.title}`}
            >
              Resolve
            </button>
          )}
        </>
      )}
    </li>
  );
}

// ─── #43: Add-Risk form ───────────────────────────────────────────────────────
//
// The backend /risks contract uses `description` (the human-readable risk),
// `severity` (low|medium|high|critical) and a required `likelihood`
// (rare|unlikely|possible|likely|certain). The frontend Risk type stores the
// text as `title`; we send it as `description` so both the register and the
// resolve flow line up with the ZeroDB rows.

const RISK_SEVERITY_OPTIONS = ["low", "medium", "high", "critical"] as const;
const RISK_LIKELIHOOD_OPTIONS = ["rare", "unlikely", "possible", "likely", "certain"] as const;

/** Raw shape of POST /risks response — { risk: { entity_id, ... } }. */
interface CreatedRiskResponse {
  risk?: { entity_id?: string; description?: string; severity?: string; status?: string };
}

function AddRiskForm({
  projectRef,
  onRiskAdded,
  api,
}: {
  projectRef: string;
  onRiskAdded: (risk: Risk) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<(typeof RISK_SEVERITY_OPTIONS)[number]>("medium");
  const [likelihood, setLikelihood] =
    useState<(typeof RISK_LIKELIHOOD_OPTIONS)[number]>("possible");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const reset = () => {
    setDescription("");
    setSeverity("medium");
    setLikelihood("possible");
    setErrorMsg(null);
  };

  const handleSubmit = async () => {
    const text = description.trim();
    if (!text) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const resp = await api.call<CreatedRiskResponse>("/risks", {
        method: "POST",
        body: {
          project_ref: projectRef,
          description: text,
          severity,
          likelihood,
        },
      });
      const created = resp.risk;
      // Map the created row back into the UI Risk shape. Severity may be
      // "critical" on the backend; the UI badge classes cover low/medium/high,
      // so clamp critical → high for display while keeping the real value stored.
      const uiSeverity = (severity === "critical" ? "high" : severity) as Risk["severity"];
      const newRisk: Risk = {
        id: created?.entity_id ?? `risk:${text.slice(0, 8)}-${severity}`,
        projectRef,
        title: text,
        severity: uiSeverity,
        status: "open",
      };
      onRiskAdded(newRisk);
      reset();
      setOpen(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to add risk");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        className="risk-add-btn"
        onClick={() => setOpen(true)}
        aria-label="Add a risk"
      >
        + Add Risk
      </button>
    );
  }

  return (
    <div className="risk-add-form" role="region" aria-label="Add a risk">
      <label className="risk-add-form__label" htmlFor="add-risk-desc">
        Risk description
      </label>
      <textarea
        id="add-risk-desc"
        className="risk-add-form__textarea"
        placeholder="Describe the risk (e.g. Permit approval slipping past construction start)..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={submitting}
        aria-label="Risk description"
      />
      <div className="risk-add-form__selects">
        <label className="risk-add-form__field">
          <span className="risk-add-form__field-label">Severity</span>
          <select
            className="risk-add-form__select"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as (typeof RISK_SEVERITY_OPTIONS)[number])}
            disabled={submitting}
            aria-label="Risk severity"
          >
            {RISK_SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="risk-add-form__field">
          <span className="risk-add-form__field-label">Likelihood</span>
          <select
            className="risk-add-form__select"
            value={likelihood}
            onChange={(e) =>
              setLikelihood(e.target.value as (typeof RISK_LIKELIHOOD_OPTIONS)[number])
            }
            disabled={submitting}
            aria-label="Risk likelihood"
          >
            {RISK_LIKELIHOOD_OPTIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </label>
      </div>
      {errorMsg && (
        <div className="project-chat__error" role="alert">
          <span aria-hidden="true">!</span>
          {errorMsg}
        </div>
      )}
      <div className="risk-add-form__actions">
        <button
          className="risk-resolve-panel__cancel"
          onClick={() => { setOpen(false); reset(); }}
          disabled={submitting}
          aria-label="Cancel add risk"
        >
          Cancel
        </button>
        <button
          className="risk-resolve-panel__submit"
          onClick={() => { void handleSubmit(); }}
          disabled={submitting || !description.trim()}
          aria-label="Save risk"
        >
          {submitting ? "Adding..." : "Add Risk"}
        </button>
      </div>
    </div>
  );
}

function RisksTab({
  risks,
  loading,
  error,
  projectRef,
  onRiskResolved,
  onRiskAdded,
  api,
}: {
  risks: Risk[];
  loading: boolean;
  error: string | null;
  projectRef: string;
  onRiskResolved: (riskId: string) => void;
  onRiskAdded: (risk: Risk) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  if (loading) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Risks</span>
        </div>
        <div className="panel__body" style={{ padding: "16px 18px" }}>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="skeleton skeleton-row" style={{ marginBottom: 8 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Risks</span>
        </div>
        <div className="panel__body">
          <div className="error-banner" style={{ margin: "16px 18px" }} role="alert">
            <span className="error-banner__icon">⚠</span>
            <span className="error-banner__text">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (risks.length === 0) {
    return (
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Risks</span>
        </div>
        <div className="panel__body">
          <div className="empty-state" style={{ border: "none" }}>
            <div className="empty-state__icon">✅</div>
            <div className="empty-state__title">No open risks</div>
            <div className="empty-state__desc">
              Identified risks and their mitigation status will appear here.
            </div>
          </div>
          <div className="risk-list__footer">
            <AddRiskForm projectRef={projectRef} onRiskAdded={onRiskAdded} api={api} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Risks</span>
        <span className="panel__count">{risks.length}</span>
      </div>
      <div className="panel__body">
        <ul className="risk-list" aria-label="Project risks">
          {risks.map((risk) => (
            <RiskItem
              key={risk.id}
              risk={risk}
              projectRef={projectRef}
              onResolved={onRiskResolved}
              api={api}
            />
          ))}
        </ul>
        <div className="risk-list__footer">
          <AddRiskForm projectRef={projectRef} onRiskAdded={onRiskAdded} api={api} />
        </div>
      </div>
    </div>
  );
}

// ─── A10: Knowledge / Chat (wired to POST /brain) ─────────────────────────────

type ChatStatus = "idle" | "loading" | "success" | "error";

function KnowledgeTab({
  projectName,
  projectId,
  api,
}: {
  projectName: string;
  projectId: string;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [answer, setAnswer] = useState("");
  const [results, setResults] = useState<BrainResult[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const currentQueryRef = useRef<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const submitQuery = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed) return;

      // Prepend project context so the brain search is scoped
      const contextualQuery = `[Project: ${projectName} (${projectId})] ${trimmed}`;
      setActiveQuery(trimmed);
      setStatus("loading");
      setAnswer("");
      setResults([]);
      setErrorMsg("");
      currentQueryRef.current = trimmed;

      try {
        const resp = await api.call<BrainResponse>("/brain", {
          method: "POST",
          body: { query: contextualQuery },
        });

        if (currentQueryRef.current !== trimmed) return;

        setAnswer(resp.answer ?? resp.context ?? "");
        setResults(resp.results ?? []);
        setStatus("success");
      } catch (err) {
        if (currentQueryRef.current !== trimmed) return;
        setErrorMsg(err instanceof Error ? err.message : "Query failed");
        setStatus("error");
      }
    },
    [api, projectName, projectId],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submitQuery(input);
    }
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Knowledge &amp; Chat</span>
      </div>
      <div className="panel__body">
        <div className="project-chat">
          {/* Messages area */}
          <div className="project-chat__messages" aria-live="polite" aria-atomic="false">
            {status === "idle" && (
              <div className="project-chat__hint">
                <div className="project-chat__hint-icon">🧠</div>
                <div className="project-chat__hint-text">
                  Ask anything about{" "}
                  <strong style={{ color: "var(--c-text-secondary)" }}>{projectName}</strong>.
                  <br />
                  The AI will search documents, milestones, decisions, and memory.
                </div>
              </div>
            )}

            {status === "loading" && (
              <div
                className="project-chat__thinking"
                role="status"
                aria-label="Searching project knowledge"
              >
                <div className="project-chat__dots" aria-hidden="true">
                  <span className="project-chat__dot" />
                  <span className="project-chat__dot" />
                  <span className="project-chat__dot" />
                </div>
                <span>
                  Searching for{" "}
                  <em style={{ color: "var(--c-text-secondary)" }}>
                    &ldquo;{activeQuery}&rdquo;
                  </em>
                </span>
              </div>
            )}

            {status === "success" && (
              <div className="project-chat__answer" role="region" aria-label="Project knowledge answer">
                <div className="project-chat__answer-label">Answer</div>
                <div className="project-chat__answer-text">
                  {answer || (
                    <span style={{ color: "var(--c-text-muted)", fontStyle: "italic" }}>
                      No answer generated — see sources below.
                    </span>
                  )}
                </div>
                {results.length > 0 && (
                  <div className="project-chat__sources">
                    <div className="project-chat__sources-label">
                      Sources ({results.length})
                    </div>
                    {results.slice(0, 5).map((r, i) => (
                      <div key={r.id} className="project-chat__source" role="listitem">
                        <span style={{ color: "var(--c-accent)", fontFamily: "var(--font-mono)", fontSize: 10 }}>
                          [{i + 1}]
                        </span>{" "}
                        {r.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {status === "error" && (
              <div className="project-chat__error" role="alert">
                <span aria-hidden="true" style={{ flexShrink: 0 }}>!</span>
                {errorMsg}
              </div>
            )}
          </div>

          {/* Input row */}
          <div className="project-chat__input-row">
            <input
              ref={inputRef}
              type="text"
              className="project-chat__input"
              placeholder="Ask about this project..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={status === "loading"}
              aria-label="Chat with project knowledge"
            />
            <button
              className="project-chat__send"
              onClick={() => { void submitQuery(input); }}
              disabled={status === "loading" || !input.trim()}
              aria-label="Send question"
            >
              {status === "loading" ? "..." : "Ask"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── A6: Agent Assessment sidebar card ───────────────────────────────────────

function AgentAssessmentCard({
  loading,
  health,
}: {
  loading: boolean;
  health: AgentHealth | null;
}) {
  // Hide entirely if not loading and no data
  if (!loading && health === null) return null;

  const scoreColor = () => {
    if (!health?.score && !health?.health) return "neutral";
    const h = (health.health ?? "").toLowerCase();
    if (h === "green" || (health.score ?? 0) >= 70) return "green";
    if (h === "yellow" || (health.score ?? 0) >= 40) return "yellow";
    return "red";
  };

  const recList = health?.recommendations ?? health?.nextSteps ?? [];
  const riskFactors = health?.riskFactors ?? [];

  return (
    <div className="panel">
      <div className="panel__head">
        <span className="panel__title">Agent Assessment</span>
        <span
          className="status-chip"
          style={{ color: "var(--c-accent)", borderColor: "rgba(245,166,35,0.3)" }}
        >
          AI
        </span>
      </div>
      <div className="panel__body">
        <div className="agent-assessment">
          {loading && (
            <div className="agent-assessment__loading" aria-label="Loading agent assessment">
              <span className="agent-assessment__spinner" aria-hidden="true" />
              <span>Analyzing project health...</span>
            </div>
          )}

          {!loading && health && (
            <>
              {(health.score !== undefined || health.health) && (
                <div className="agent-assessment__score-row">
                  <span className="agent-assessment__score-label">Health</span>
                  <span className={`agent-assessment__score-value agent-assessment__score-value--${scoreColor()}`}>
                    {health.score !== undefined
                      ? `${health.score}/100`
                      : (health.health ?? "—")}
                  </span>
                </div>
              )}

              {health.summary && (
                <div className="agent-assessment__section">
                  <div className="agent-assessment__section-title">Summary</div>
                  <p className="agent-assessment__summary">{health.summary}</p>
                </div>
              )}

              {riskFactors.length > 0 && (
                <div className="agent-assessment__section">
                  <div className="agent-assessment__section-title">Risk Factors</div>
                  <ul className="agent-assessment__list" aria-label="Risk factors">
                    {riskFactors.map((rf, i) => (
                      <li key={i} className="agent-assessment__list-item">{rf}</li>
                    ))}
                  </ul>
                </div>
              )}

              {recList.length > 0 && (
                <div className="agent-assessment__section">
                  <div className="agent-assessment__section-title">Recommended Next Steps</div>
                  <ul className="agent-assessment__list" aria-label="Recommended next steps">
                    {recList.map((rec, i) => (
                      <li key={i} className="agent-assessment__list-item">{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── #43: Project phase editor ────────────────────────────────────────────────
//
// Mirrors the backend ALLOWED_PHASE_TRANSITIONS map so the UI only offers legal
// moves (an illegal PATCH returns 409). Forward one step, plus a one-step
// regression (rework) where the backend permits it.

type ProjectPhaseName =
  | "acquisition"
  | "design"
  | "permitting"
  | "construction"
  | "completion";

const ALLOWED_PHASE_TRANSITIONS: Record<ProjectPhaseName, readonly ProjectPhaseName[]> = {
  acquisition: ["design"],
  design: ["permitting", "acquisition"],
  permitting: ["construction", "design"],
  construction: ["completion", "permitting"],
  completion: [],
};

function nextPhasesFor(phase: string): readonly ProjectPhaseName[] {
  return (ALLOWED_PHASE_TRANSITIONS as Record<string, readonly ProjectPhaseName[]>)[phase] ?? [];
}

/** Raw shape of PATCH /projects/:id/status — { project: {...} }. */
interface ProjectStatusResponse {
  project?: Record<string, unknown>;
}

function ProjectPhaseEditor({
  project,
  projectRef,
  onPhaseChanged,
  api,
}: {
  project: ProjectDetail;
  projectRef: string;
  onPhaseChanged: (phase: string) => void;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<ProjectPhaseName | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const options = nextPhasesFor(project.phase);

  // Terminal or unknown phase — nothing to transition to.
  if (options.length === 0) return null;

  const handleSubmit = async () => {
    if (!target) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const resp = await api.call<ProjectStatusResponse>(
        `/projects/${encodeURIComponent(projectRef)}/status`,
        { method: "PATCH", body: { phase: target } },
      );
      const newPhase = (resp.project?.["phase"] as string) ?? target;
      onPhaseChanged(newPhase);
      setOpen(false);
      setTarget("");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to change phase");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        className="phase-edit-btn"
        onClick={() => setOpen(true)}
        aria-label="Change project phase"
      >
        Change phase
      </button>
    );
  }

  return (
    <div className="phase-edit-form" role="region" aria-label="Change project phase">
      <label className="phase-edit-form__label" htmlFor="phase-select">
        Move to phase
      </label>
      <select
        id="phase-select"
        className="phase-edit-form__select"
        value={target}
        onChange={(e) => setTarget(e.target.value as ProjectPhaseName)}
        disabled={submitting}
        aria-label="Target phase"
      >
        <option value="">Select…</option>
        {options.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      {errorMsg && (
        <div className="project-chat__error" role="alert">
          <span aria-hidden="true">!</span>
          {errorMsg}
        </div>
      )}
      <div className="phase-edit-form__actions">
        <button
          className="risk-resolve-panel__cancel"
          onClick={() => { setOpen(false); setTarget(""); setErrorMsg(null); }}
          disabled={submitting}
          aria-label="Cancel phase change"
        >
          Cancel
        </button>
        <button
          className="risk-resolve-panel__submit"
          onClick={() => { void handleSubmit(); }}
          disabled={submitting || !target}
          aria-label="Save phase change"
        >
          {submitting ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

// ─── Info sidebar ─────────────────────────────────────────────────────────────

function InfoSidebar({
  project,
  projectRef,
  onPhaseChanged,
  agentHealthLoading,
  agentHealth,
  api,
}: {
  project: ProjectDetail;
  projectRef: string;
  onPhaseChanged: (phase: string) => void;
  agentHealthLoading: boolean;
  agentHealth: AgentHealth | null;
  api: ReturnType<typeof import("../providers/AINativeProvider.js").useAINative>["api"];
}) {
  return (
    <div className="info-sidebar">
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Project Info</span>
        </div>
        <div className="panel__body">
          <div className="info-row">
            <span className="info-row__label">Health</span>
            <span
              className={`health-badge ${healthClass(project.health)}`}
              aria-label={`Health: ${healthLabel(project.health)}`}
            >
              <span className="health-dot" aria-hidden="true" />
              {healthLabel(project.health)}
            </span>
          </div>
          <div className="info-row">
            <span className="info-row__label">Phase</span>
            <span className="info-row__value">
              <span className="status-chip">{project.phase}</span>
            </span>
          </div>
          <div className="info-row info-row--action">
            <ProjectPhaseEditor
              project={project}
              projectRef={projectRef}
              onPhaseChanged={onPhaseChanged}
              api={api}
            />
          </div>
          <div className="info-row">
            <span className="info-row__label">Status</span>
            <span className="info-row__value">{project.status}</span>
          </div>
          {project.address && (
            <div className="info-row">
              <span className="info-row__label">Address</span>
              <span className="info-row__value" style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                {project.address}
              </span>
            </div>
          )}
          {project.budget && (
            <>
              <div className="info-row">
                <span className="info-row__label">Total Budget</span>
                <span
                  className="info-row__value"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {formatCurrency(project.budget.total)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-row__label">Spent</span>
                <span
                  className="info-row__value"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {formatCurrency(project.budget.spent)}
                </span>
              </div>
            </>
          )}
          {project.createdAt && (
            <div className="info-row">
              <span className="info-row__label">Created</span>
              <span className="info-row__value">{formatDate(project.createdAt)}</span>
            </div>
          )}
        </div>
      </div>

      {/* A6: Agent Assessment card — only renders if loading or has data */}
      <AgentAssessmentCard loading={agentHealthLoading} health={agentHealth} />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

/** Raw shape of GET /projects/:id — backend returns { project, health } wrapper */
interface RawProjectResponse {
  project?: Record<string, unknown>;
  health?: Record<string, unknown>;
  // Fallback: some fields at top-level for older clients
  [key: string]: unknown;
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { api } = useAINative();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);

  const [projectLoading, setProjectLoading] = useState(true);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [milestonesLoading, setMilestonesLoading] = useState(true);
  const [milestonesError, setMilestonesError] = useState<string | null>(null);
  const [risksLoading, setRisksLoading] = useState(true);
  const [risksError, setRisksError] = useState<string | null>(null);

  // A6: agent health — separate card-only loading state
  const [agentHealthLoading, setAgentHealthLoading] = useState(false);
  const [agentHealth, setAgentHealth] = useState<AgentHealth | null>(null);

  const [activeTab, setActiveTab] = useState<TabKey>("timeline");

  // Use a ref to capture the current api object so fetchAll and health fetch
  // only depend on [id], not [api]. This prevents infinite re-renders in tests
  // where the mock returns a new api object on every render.
  const apiRef = useRef(api);
  apiRef.current = api;

  const fetchAll = useCallback(async () => {
    if (!id) return;

    // Project detail — unwrap { project, health } envelope (#50 fix)
    setProjectLoading(true);
    setProjectError(null);
    const rawResp = await safeApiCall(() =>
      apiRef.current.call<RawProjectResponse>(`/projects/${encodeURIComponent(id)}`),
    );

    let resolvedProject: ProjectDetail | null = null;

    if (rawResp) {
      // Check if the response is wrapped in { project, health }
      if (rawResp.project && typeof rawResp.project === "object") {
        const proj = rawResp.project as Record<string, unknown>;
        const healthData = rawResp.health as Record<string, unknown> | undefined;
        // Merge health color from the health wrapper if present
        resolvedProject = {
          ...proj,
          id: (proj.id ?? proj.project_id ?? proj.entity_id ?? id) as string,
          name: (proj.name ?? id) as string,
          phase: (proj.phase ?? "acquisition") as ProjectDetail["phase"],
          status: (proj.status ?? "active") as string,
          health: ((healthData?.health ?? proj.health ?? "green") as string).toLowerCase() as ProjectDetail["health"],
          milestoneProgress: (proj.milestoneProgress ?? 0) as number,
          milestoneCount: (proj.milestoneCount ?? 0) as number,
          milestoneDone: (proj.milestoneDone ?? 0) as number,
          // documents / quotes / budgetLines are siblings of `project` in the
          // { project, health, documents, quotes, budgetLines } envelope — pull
          // them from the top level so the tabs render persisted data on load.
          ...(rawResp.documents !== undefined ? { documents: rawResp.documents } : {}),
          ...(rawResp.quotes !== undefined ? { quotes: rawResp.quotes } : {}),
          ...(rawResp.budgetLines !== undefined ? { budgetLines: rawResp.budgetLines } : {}),
        } as ProjectDetail;
      } else if (!rawResp.project && (rawResp.name || rawResp.entity_id)) {
        // Flat response (legacy)
        resolvedProject = rawResp as unknown as ProjectDetail;
      }
    }

    if (resolvedProject) {
      setProject(resolvedProject);
    } else {
      setProjectError("Could not load project. The endpoint may be unavailable.");
    }
    setProjectLoading(false);

    // Milestones — may 404 if endpoint not yet wired
    setMilestonesLoading(true);
    setMilestonesError(null);
    const milestonesData = await safeApiCall(() =>
      apiRef.current.call<Milestone[] | { milestones: Milestone[] }>(
        `/projects/${encodeURIComponent(id)}/milestones`,
      ),
    );
    if (milestonesData !== null) {
      const list = Array.isArray(milestonesData)
        ? milestonesData
        : (milestonesData as { milestones: Milestone[] }).milestones ?? [];
      // Normalize milestone IDs — backend may use entity_id
      setMilestones(list.map((m) => ({
        ...m,
        id: m.id ?? ((m as unknown as Record<string, unknown>).entity_id as string) ?? crypto.randomUUID(),
      })));
    } else {
      setMilestonesError("Milestones unavailable.");
      setMilestones([]);
    }
    setMilestonesLoading(false);

    // Risks — cross-referenced by project id
    setRisksLoading(true);
    setRisksError(null);
    const risksData = await safeApiCall(() =>
      apiRef.current.call<Risk[] | { risks: Risk[] }>(`/risks?project_ref=${encodeURIComponent(id)}`),
    );
    if (risksData !== null) {
      const list = Array.isArray(risksData)
        ? risksData
        : (risksData as { risks: Risk[] }).risks ?? [];
      // Normalize risk IDs
      setRisks(list.map((r) => ({
        ...r,
        id: r.id ?? ((r as unknown as Record<string, unknown>).entity_id as string) ?? crypto.randomUUID(),
      })));
    } else {
      setRisksError("Risks unavailable.");
      setRisks([]);
    }
    setRisksLoading(false);
  }, [id]); // intentionally only re-runs when id changes; uses apiRef to read current api

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // Run health fetch once per id (uses apiRef to avoid dep on unstable api object)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setAgentHealthLoading(true);
    setAgentHealth(null);
    apiRef.current
      .call<AgentHealth>(`/agents/development/health/${encodeURIComponent(id)}`)
      .then((data) => {
        if (!cancelled) setAgentHealth(data ?? null);
      })
      .catch(() => {
        if (!cancelled) setAgentHealth(null);
      })
      .finally(() => {
        if (!cancelled) setAgentHealthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]); // intentionally only re-runs when id changes

  // A9: optimistic risk resolve — called by RiskItem after successful API call
  const handleRiskResolved = useCallback((riskId: string) => {
    setRisks((prev) =>
      prev.map((r) =>
        r.id === riskId ? { ...r, status: "resolved" as const } : r,
      ),
    );
  }, []);

  // #43: optimistic writes — prepend/patch the local lists after a successful POST/PATCH.
  const handleRiskAdded = useCallback((risk: Risk) => {
    setRisks((prev) => [risk, ...prev]);
    setRisksError(null);
  }, []);

  const handleMilestoneAdded = useCallback((m: Milestone) => {
    setMilestones((prev) => [...prev, m]);
    setMilestonesError(null);
  }, []);

  const handleMilestoneUpdated = useCallback((m: Milestone) => {
    setMilestones((prev) => prev.map((existing) => (existing.id === m.id ? m : existing)));
  }, []);

  const handlePhaseChanged = useCallback((phase: string) => {
    setProject((prev) => (prev ? { ...prev, phase: phase as ProjectDetail["phase"] } : prev));
  }, []);

  // ── Missing ID guard ───────────────────────────────────────────────────────
  if (!id) {
    return (
      <main>
        <div className="error-banner" role="alert">
          <span className="error-banner__icon">⚠</span>
          <span className="error-banner__text">No project ID provided.</span>
        </div>
      </main>
    );
  }

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (projectLoading) {
    return (
      <main aria-busy="true" aria-label="Loading project">
        <div className="detail-header">
          <div className="skeleton" style={{ width: 80, height: 32, borderRadius: 4 }} />
          <div className="detail-title-wrap">
            <div className="skeleton" style={{ width: "40%", height: 32, borderRadius: 4, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: "60%", height: 14, borderRadius: 4 }} />
          </div>
        </div>
        <div className="skeleton" style={{ height: 44, borderRadius: 8, marginBottom: 28 }} />
        <div className="skeleton skeleton-card" />
      </main>
    );
  }

  // ── Fatal error ────────────────────────────────────────────────────────────
  if (projectError || !project) {
    return (
      <main>
        <div className="detail-header">
          <button
            className="detail-back"
            onClick={() => void navigate("/projects")}
            aria-label="Back to Projects"
          >
            ← Projects
          </button>
        </div>
        <div className="error-banner" role="alert">
          <span className="error-banner__icon">⚠</span>
          <span className="error-banner__text">
            {projectError ?? `Project "${id}" not found.`}
          </span>
          <button className="error-banner__retry" onClick={() => void fetchAll()}>
            Retry
          </button>
        </div>
      </main>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <main>
      {/* Header */}
      <div className="detail-header">
        <button
          className="detail-back"
          onClick={() => void navigate("/projects")}
          aria-label="Back to Projects"
        >
          ← Projects
        </button>
        <div className="detail-title-wrap">
          <h1 className="detail-title">{project.name}</h1>
          <div className="detail-meta">
            <span
              className={`health-badge ${healthClass(project.health)}`}
              aria-label={`Health: ${healthLabel(project.health)}`}
            >
              <span className="health-dot" aria-hidden="true" />
              {healthLabel(project.health)}
            </span>
            <span className="status-chip">{project.phase}</span>
            {project.address && (
              <span className="detail-meta__item">{project.address}</span>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* Content grid: main + sidebar */}
      <div className="detail-grid">
        {/* Main panel */}
        <div
          id={`tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === "timeline" && (
            <MilestoneTimeline
              milestones={milestones}
              loading={milestonesLoading}
              error={milestonesError}
              projectRef={id}
              onMilestoneAdded={handleMilestoneAdded}
              onMilestoneUpdated={handleMilestoneUpdated}
              api={api}
            />
          )}
          {activeTab === "documents" && <DocumentsTab project={project} projectRef={id} api={api} />}
          {activeTab === "quotes" && <QuotesTab project={project} projectRef={id} api={api} />}
          {activeTab === "budget" && <BudgetTab project={project} projectRef={id} api={api} />}
          {activeTab === "risks" && (
            <RisksTab
              risks={risks}
              loading={risksLoading}
              error={risksError}
              projectRef={id}
              onRiskResolved={handleRiskResolved}
              onRiskAdded={handleRiskAdded}
              api={api}
            />
          )}
          {activeTab === "knowledge" && (
            <KnowledgeTab
              projectName={project.name}
              projectId={id}
              api={api}
            />
          )}
        </div>

        {/* Info sidebar */}
        <InfoSidebar
          project={project}
          projectRef={id}
          onPhaseChanged={handlePhaseChanged}
          agentHealthLoading={agentHealthLoading}
          agentHealth={agentHealth}
          api={api}
        />
      </div>
    </main>
  );
}
